import { TUNER } from '@/config/audio';
import {
  frequencyToMidi,
  midiToFrequency,
  PITCH_CLASS_NAMES,
  STANDARD_TUNING_MIDI,
  indexToStringNumber,
} from '@/data/tuning';
import type { PitchReading } from '@/types/audio';
import type { StringNumber } from '@/types/music';
import { median } from '@/audio/dsp/stats';

/** What the tuner screen shows. Everything nullable: silence is a valid state. */
export interface TunerDisplay {
  /** e.g. "A2". Null while nothing is heard. */
  noteName: string | null;
  /** Signed distance to the nearest note, in cents. */
  cents: number | null;
  /** |cents| within the in-tune band. */
  inTune: boolean;
  /** The standard-tuning string this reading is closest to, if any is near. */
  stringNumber: StringNumber | null;
}

const EMPTY: TunerDisplay = { noteName: null, cents: null, inTune: false, stringNumber: null };

/** How far from a string's nominal pitch a reading may sit and still light it up. */
const STRING_MATCH_CENTS = 120;

/** Consecutive silent readings before the display lets go of the last note. */
const HOLD_FRAMES = 3;

/**
 * Steadies raw pitch readings into something a hand can follow.
 *
 * A raw YIN stream flickers: the attack of a pluck reads sharp for a frame or
 * two (the string is literally tighter while it is being displaced), and decay
 * tails wobble. The median over a short window shows where the note actually
 * sits, and a short hold keeps the display from blinking between plucks.
 */
export class TunerSmoother {
  private readonly recent: number[] = [];
  private silentFrames = 0;
  private last: TunerDisplay = EMPTY;

  push(reading: PitchReading): TunerDisplay {
    if (reading.frequency === null) {
      this.silentFrames++;
      if (this.silentFrames >= HOLD_FRAMES) {
        this.recent.length = 0;
        this.last = EMPTY;
      }
      return this.last;
    }

    this.silentFrames = 0;
    // Median in the log-frequency domain: cents are what the player reads, and
    // an outlier octave frame must not drag an average around.
    this.recent.push(frequencyToMidi(reading.frequency));
    if (this.recent.length > TUNER.smoothingFrames) this.recent.shift();

    const midiExact = median(this.recent);
    const nearest = Math.round(midiExact);
    const cents = (midiExact - nearest) * 100;

    this.last = {
      noteName: nameOf(nearest),
      cents,
      inTune: Math.abs(cents) <= TUNER.inTuneCents,
      stringNumber: nearestString(midiExact),
    };
    return this.last;
  }

  reset(): void {
    this.recent.length = 0;
    this.silentFrames = 0;
    this.last = EMPTY;
  }
}

/** "A2", "F#3" — pitch-class name plus scientific octave. */
export function nameOf(midi: number): string {
  const octave = Math.floor(midi / 12) - 1;
  return `${PITCH_CLASS_NAMES[((midi % 12) + 12) % 12]}${octave}`;
}

function nearestString(midiExact: number): StringNumber | null {
  let best: StringNumber | null = null;
  let bestCents = Infinity;
  for (let index = 0; index < STANDARD_TUNING_MIDI.length; index++) {
    const cents = Math.abs(midiExact - STANDARD_TUNING_MIDI[index]) * 100;
    if (cents < bestCents) {
      bestCents = cents;
      best = indexToStringNumber(index);
    }
  }
  return bestCents <= STRING_MATCH_CENTS ? best : null;
}

/** Nominal frequency of a standard-tuning string, for the string row. */
export function stringFrequency(stringNumber: StringNumber): number {
  return midiToFrequency(STANDARD_TUNING_MIDI[6 - stringNumber]);
}
